import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import Box from '@mui/material/Box'
// import ImageWithSkeleton from '../../../../../../../components/ImageWithSkeleton'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import SettingsIcon from '@mui/icons-material/Settings'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import { useTheme } from '@mui/material/styles'
import MapViewer from '../../../../../../../components/Maps/MapViewer'
import useMediaQuery from '@mui/material/useMediaQuery'
import CourseMapDrawer from './CourseMapDrawer'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export default function CourseMap ({ race, distance, showMapDefault }) {
  const { t } = useTranslation('raceGuide')
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [showMap, setShowMap] = useState(showMapDefault)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [showDistanceMarkers, setShowDistanceMarkers] = useState(true)
  const [showNames, setShowNames] = useState(true)
  const [distanceInterval, setDistanceInterval] = useState(distance > 55 ? 5 : 1)
  const [showUserLocation, setShowUserLocation] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const [locationPermissionDialogOpen, setLocationPermissionDialogOpen] = useState(false)
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false)
  const [lastLocationFetchTime, setLastLocationFetchTime] = useState(0)
  const mapRef = useRef(null)

  // Memoize race distance data to prevent unnecessary recalculations
  const { /*mapImage, */ gpx, color, checkpoints } = useMemo(() => {
    if (!race?.raceDistances) {
      return {}
    }
    // Handle type mismatch: compare both as strings for flexibility
    const distanceStr = String(distance)

    const foundRace = race.raceDistances.find(r => {
      const raceDistanceStr = String(r.distance)
      return raceDistanceStr === distanceStr
    })

    return foundRace || {}
  }, [race, distance, showMapDefault])

  const gpxFile = useMemo(() => `/gpx/goat/${gpx}`, [gpx])

  // Memoized helper functions
  const areStringsAlike = useCallback((str1, str2) => {
    if (!str1 || !str2) return false
    const lowerStr1 = str1.toLowerCase()
    const lowerStr2 = str2.toLowerCase()
    return lowerStr1.includes(lowerStr2) || lowerStr2.includes(lowerStr1)
  }, [])

  const formatLocationLabel = useCallback((location) => {
    if (!location) return ''
    return location.cp
      ? (location.cp.toLowerCase() === 'start' || location.cp.toLowerCase() === 'finish')
        ? location.cp
        : `CP${location.cp}: ${location.name}`
      : location.name || ''
  }, [])

  const handleLocationClick = useCallback((location) => {
    if (location?.gpxLocation?.lat && location?.gpxLocation?.lng && mapRef.current) {
      mapRef.current.setView([parseFloat(location.gpxLocation.lat), parseFloat(location.gpxLocation.lng)], 14)
      setTimeout(() => {
        setDrawerOpen(false)
      }, 300)
    }
  }, [mapRef])

  // Geolocation functions
  const requestLocationPermission = useCallback(() => {
    if (!navigator.geolocation) {
      alert(t('courseMap.geolocationNotSupported'))
      return false
    }
    return true
  }, [t])

  const fetchUserLocation = useCallback(() => {
    if (!locationPermissionGranted || !showUserLocation) return

    const now = Date.now()
    if (now - lastLocationFetchTime < 60000) return // 1 minute interval

    if (!navigator.geolocation) {
      console.error('Geolocation API not available')
      setShowUserLocation(false)
      alert(t('courseMap.geolocationNotSupported'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setUserLocation({ lat: latitude, lng: longitude })
        setLastLocationFetchTime(now)

        // If this is the first successful location fetch, center map on user
        if (!userLocation && mapRef.current) {
          mapRef.current.setView([latitude, longitude], 14)
        }
      },
      (error) => {
        console.error('Error fetching location:', error)
        let errorKey = 'courseMap.unableRetrieveUnknown'
        let errorVars

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorKey = 'courseMap.unableRetrieveDenied'
            setLocationPermissionGranted(false)
            setShowUserLocation(false)
            break
          case error.POSITION_UNAVAILABLE:
            errorKey = 'courseMap.unableRetrieveUnavailable'
            break
          case error.TIMEOUT:
            errorKey = 'courseMap.unableRetrieveTimeout'
            break
          default:
            errorVars = { message: error.message || t('courseMap.unknownError') }
        }

        alert(t(errorKey, errorVars))
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  }, [locationPermissionGranted, showUserLocation, lastLocationFetchTime, userLocation, t])

  const handleLocationButtonClick = useCallback(() => {
    if (locationPermissionGranted) {
      // If permission already granted, toggle location display
      setShowUserLocation(!showUserLocation)
      if (!showUserLocation) {
        fetchUserLocation()
      }
    } else {
      // Request permission
      if (requestLocationPermission()) {
        setLocationPermissionDialogOpen(true)
      }
    }
  }, [locationPermissionGranted, showUserLocation, fetchUserLocation, requestLocationPermission])

  const handlePermissionDialogConfirm = useCallback(() => {
    setLocationPermissionDialogOpen(false)

    if (!navigator.geolocation) {
      alert(t('courseMap.geolocationNotSupported'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationPermissionGranted(true)
        setShowUserLocation(true)
        const { latitude, longitude } = position.coords
        setUserLocation({ lat: latitude, lng: longitude })
        setLastLocationFetchTime(Date.now())

        // Center map on user location
        if (mapRef.current) {
          mapRef.current.setView([latitude, longitude], 14)
        }
      },
      (error) => {
        console.error('Permission denied or error:', error)
        let errorKey = 'courseMap.accessDeniedUnknown'
        let errorVars

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorKey = 'courseMap.accessDeniedDenied'
            break
          case error.POSITION_UNAVAILABLE:
            errorKey = 'courseMap.accessDeniedUnavailable'
            break
          case error.TIMEOUT:
            errorKey = 'courseMap.accessDeniedTimeout'
            break
          default:
            errorVars = { message: error.message || t('courseMap.unknownError') }
        }

        alert(t(errorKey, errorVars))
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  }, [t])

  const handlePermissionDialogCancel = useCallback(() => {
    setLocationPermissionDialogOpen(false)
  }, [])

  // Fetch location when map is opened or when showUserLocation changes
  useEffect(() => {
    if (showMap && showUserLocation && locationPermissionGranted) {
      fetchUserLocation()

      // Set up interval for periodic updates
      const intervalId = setInterval(fetchUserLocation, 60000)

      return () => clearInterval(intervalId)
    }
  }, [showMap, showUserLocation, locationPermissionGranted, fetchUserLocation])

  // Clean up location tracking when component unmounts or map closes
  useEffect(() => {
    return () => {
      // Clear any existing intervals when component unmounts
      // This prevents memory leaks
    }
  }, [])

  // Reset location when map is closed
  useEffect(() => {
    if (!showMap) {
      // Optionally reset location when map closes
      // setUserLocation(null)
    }
  }, [showMap])

  // Memoize locations calculation
  const locations = useMemo(() => {
    const loc = race?.locations || []
    return checkpoints?.map(checkpoint => {
      const matchingLocation = loc.find(location =>
        location.name && checkpoint.name &&
        areStringsAlike(location.name, checkpoint.name)
      )
      return {
        ...checkpoint,
        gpxLocation: matchingLocation?.gpxLocation || null
      }
    }) || []
  }, [race, checkpoints, areStringsAlike])

  // Handle location switch toggle
  const handleLocationSwitchToggle = useCallback(() => {
    if (showUserLocation) {
      // Turning off - clear user location and stop tracking
      setUserLocation(null)
      setShowUserLocation(false)
    } else {
      // Turning on - start tracking if permission granted
      if (locationPermissionGranted) {
        setShowUserLocation(true)
        fetchUserLocation()
      }
    }
  }, [showUserLocation, locationPermissionGranted, fetchUserLocation])

  return (
    <Box>
      {gpx &&
        <Button
          sx={{ my: 3, py: 1.5, px: 4, fontWeight: 'bold', borderRadius: 2, boxShadow: 2 }}
          onClick={() => setShowMap(true)}
          variant='contained'
          color='primary'
        >
          {t('courseMap.openInteractiveMap')}
        </Button>}
      {/* <ImageWithSkeleton {...{ src: `https://d1hi0tbu2kom2c.cloudfront.net${mapImage}`, maxWidth: 720 }} square oneSize /> */}

      {showMap &&
        <Dialog
          open={!!showMap}
          onClose={() => {
            setShowMap(null)
            if (showMapDefault) {
              navigate(`/races/${race.slug}/details`)
            }
          }}
          fullScreen={isMobile}
          maxWidth=''
          fullWidth
          PaperProps={{ sx: { backgroundColor: 'transparent', boxShadow: 'none', zIndex: 1500 } }}
          sx={{ p: isMobile ? 0 : 2, zIndex: 1500 }}
        >
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <IconButton
              aria-label={t('shared.close')}
              onClick={() => {
                setShowMap(null)
                if (showMapDefault) {
                  navigate(`/races/${race.slug}/details`)
                }
              }}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                mr: { xs: 1, sm: 2 },
                mt: { xs: 1, sm: 2 },
                zIndex: 1500,
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
              }}
            >
              <CloseIcon />
            </IconButton>

            <IconButton
              aria-label={t('courseMap.settingsAria')}
              onClick={() => setDrawerOpen(true)}
              sx={{
                position: 'absolute',
                left: 32,
                bottom: 36,
                mr: { xs: 1, sm: 2 },
                mt: { xs: 1, sm: 2 },
                zIndex: 1500,
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
              }}
            >
              <SettingsIcon />
            </IconButton>

            {!showUserLocation && (
              <IconButton
                aria-label={t('courseMap.myLocationAria')}
                onClick={handleLocationButtonClick}
                sx={{
                  position: 'absolute',
                  right: 32,
                  bottom: 36,
                  mr: { xs: 1, sm: 2 },
                  mt: { xs: 1, sm: 2 },
                  zIndex: 1500,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)'
                }}
              >
                <MyLocationIcon />
              </IconButton>
            )}

            <MapViewer
              ref={mapRef}
              gpxFilePath={gpxFile}
              color={color}
              showDistanceMarkers={showDistanceMarkers}
              distanceInterval={distanceInterval}
              locations={locations}
              showNames={showNames}
              userLocation={userLocation}
            />
          </Box>
        </Dialog>}

      <CourseMapDrawer
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        showDistanceMarkers={showDistanceMarkers}
        setShowDistanceMarkers={setShowDistanceMarkers}
        distanceInterval={distanceInterval}
        setDistanceInterval={setDistanceInterval}
        showNames={showNames}
        setShowNames={setShowNames}
        locations={locations}
        handleLocationClick={handleLocationClick}
        formatLocationLabel={formatLocationLabel}
        showUserLocation={showUserLocation}
        setShowUserLocation={handleLocationSwitchToggle}
        locationPermissionGranted={locationPermissionGranted}
      />

      <Dialog
        open={locationPermissionDialogOpen}
        onClose={handlePermissionDialogCancel}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { zIndex: 3500 } }}
        sx={{ zIndex: 3500 }}
      >
        <DialogTitle>{t('courseMap.allowLocationTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant='body1' gutterBottom>
            {t('courseMap.allowLocationBody')}
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            {t('courseMap.allowLocationNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePermissionDialogCancel} color='secondary'>
            {t('courseMap.cancel')}
          </Button>
          <Button onClick={handlePermissionDialogConfirm} color='primary' variant='contained'>
            {t('courseMap.allowAccess')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
